import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import type {
  ControlCenter,
  ControlFeedItem,
} from "@/features/admin";
import { cn } from "@/lib/utils";
import {
  ChatCircleDotsIcon,
  PhoneIcon,
  PhoneOutgoingIcon,
  PlugsIcon,
  WrenchIcon,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import {
  ago,
  clock,
  flag,
  RESULT_LABEL,
  SENTIMENT_EMOJI,
  STATUS_VAR,
} from "./format";

const sessionPath = (clientId: string, sessionId: string) =>
  `/${clientId}/calls/session/${sessionId}`;

/** Who is on the line right now. The timers tick locally between two refreshes. */
export function LiveSessions({
  live,
  now,
}: {
  live: ControlCenter["live"];
  now: number;
}) {
  const total = live.calls + live.chats;
  return (
    <Card className="h-full">
      <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-sm flex items-center gap-2">
          <span className="relative flex size-2.5">
            {total > 0 ? (
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-60" />
            ) : null}
            <span
              className={cn(
                "relative inline-flex size-2.5 rounded-full",
                total > 0 ? "bg-success" : "bg-muted-foreground/40",
              )}
            />
          </span>
          En ce moment
        </CardTitle>
        <span className="text-xs text-muted-foreground tabular-nums">
          {total} conversation(s)
        </span>
      </CardHeader>
      <CardContent className="p-0">
        {live.sessions.length ? (
          <ScrollArea className="h-[360px] px-4 [&_[data-radix-scroll-area-viewport]>div]:!block">
            <ul className="space-y-1.5 pb-4">
              {live.sessions.map((s) => {
                const Icon =
                  s.medium === "call"
                    ? s.type === "outbound"
                      ? PhoneOutgoingIcon
                      : PhoneIcon
                    : ChatCircleDotsIcon;
                return (
                  <li key={s.id} className="animate-fade-in">
                    <Link
                      to={sessionPath(s.client_id, s.id)}
                      className="flex items-center gap-3 rounded-lg border bg-background/50 px-3 py-2 transition-colors hover:bg-muted"
                    >
                      <div
                        className={cn(
                          "flex size-8 shrink-0 items-center justify-center rounded-full",
                          s.medium === "call"
                            ? "bg-success-soft text-success"
                            : "bg-info-soft text-info",
                        )}
                      >
                        <Icon size={16} weight="fill" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-xs font-medium">
                          {s.client_name || s.client_id}
                        </div>
                        <div className="truncate text-[11px] text-muted-foreground">
                          {s.name || (s.medium === "call" ? "Appel" : "Chat")}
                          {s.language ? ` · ${s.language.toUpperCase()}` : ""}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-mono text-xs tabular-nums">
                          {clock(now - s.started_at)}
                        </div>
                        <div className="text-[11px] text-muted-foreground tabular-nums">
                          {s.country_code ? flag(s.country_code) : ""}{" "}
                          {s.exchanges} éch.
                        </div>
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </ScrollArea>
        ) : (
          <div className="flex h-[360px] flex-col items-center justify-center gap-2 text-sm text-muted-foreground">
            <PhoneIcon size={28} className="opacity-40" />
            Personne en ligne pour l'instant.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const FEED_ICON: Record<ControlFeedItem["kind"], typeof PhoneIcon> = {
  session: PhoneIcon,
  tool_error: WrenchIcon,
  external_error: PlugsIcon,
};

/** What just happened: conversations as they finish, incidents as they occur. */
export function EventFeed({
  feed,
  now,
}: {
  feed: ControlFeedItem[];
  now: number;
}) {
  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Fil des événements</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {feed.length ? (
          <ScrollArea className="h-[360px] px-4 [&_[data-radix-scroll-area-viewport]>div]:!block">
            <ol className="relative space-y-3 border-l pl-4 pb-4 ml-1">
              {feed.map((item) => {
                const Icon =
                  item.kind === "session" && item.medium !== "call"
                    ? ChatCircleDotsIcon
                    : FEED_ICON[item.kind];
                return (
                  <li
                    key={`${item.kind}-${item.session_id}-${item.at}`}
                    className="relative animate-fade-in"
                  >
                    <span
                      className="absolute -left-[22px] top-1 size-3 rounded-full border-2 border-card"
                      style={{ background: STATUS_VAR[item.status] }}
                    />
                    <div className="flex items-start gap-2">
                      <Icon
                        size={14}
                        className="mt-0.5 shrink-0 text-muted-foreground"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline gap-2">
                          <span className="truncate text-xs font-medium">
                            {item.kind === "session"
                              ? item.title || "Conversation"
                              : item.title}
                          </span>
                          <span className="ml-auto shrink-0 whitespace-nowrap text-[10px] text-muted-foreground">
                            {ago(item.at, now)}
                          </span>
                        </div>
                        <div className="truncate text-[11px] text-muted-foreground">
                          {item.client_id ? (
                            <Link
                              to={`/admin/clients/${item.client_id}`}
                              className="hover:underline"
                            >
                              {item.client_name || item.client_id}
                            </Link>
                          ) : null}
                          {item.kind === "session" ? (
                            <>
                              {" · "}
                              <span style={{ color: STATUS_VAR[item.status] }}>
                                {RESULT_LABEL[item.result ?? "unknown"]}
                              </span>
                              {item.sentiment
                                ? ` ${SENTIMENT_EMOJI[item.sentiment] ?? ""}`
                                : ""}
                              {item.duration_ms
                                ? ` · ${clock(item.duration_ms)}`
                                : ""}
                              {item.exchanges ? ` · ${item.exchanges} tours` : ""}
                              {item.country_code
                                ? ` · ${flag(item.country_code)}`
                                : ""}
                            </>
                          ) : item.detail ? (
                            <> · {item.detail}</>
                          ) : null}
                        </div>
                        {item.session_id && item.client_id ? (
                          <Link
                            to={sessionPath(item.client_id, item.session_id)}
                            className="text-[11px] text-primary hover:underline"
                          >
                            Ouvrir la conversation
                          </Link>
                        ) : null}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          </ScrollArea>
        ) : (
          <div className="flex h-[360px] items-center justify-center text-sm text-muted-foreground">
            Rien à signaler.
          </div>
        )}
      </CardContent>
    </Card>
  );
}
